import { format, isYesterday } from 'date-fns'
import { useEffect, useState } from 'react'

function pluralize(value: number, unit: string): string {
  return `${value} ${value === 1 ? unit : `${unit}s`} ago`
}

function computeTimeAgo(timestamp: number, isShort: boolean | undefined): string {
  const timeDiff = Date.now() - timestamp

  const seconds = Math.floor(timeDiff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  if (seconds < 60) return isShort ? 'now' : 'just now'

  if (minutes < 60) return `${minutes} min ago`

  if (hours < 24) return isShort ? `${hours}h` : pluralize(hours, 'hour')

  if (days === 1 && isYesterday(timestamp)) {
    return isShort ? '1d' : `yesterday, ${format(new Date(timestamp), 'h:mm a')}`
  }

  if (days < 7) return isShort ? `${days}d` : format(new Date(timestamp), 'MMMM do, yyyy h:mm a')

  if (weeks < 4) return pluralize(weeks, 'week')

  if (months < 12) return pluralize(months, 'month')

  return pluralize(years, 'year')
}

export function useTimeAgo(timestamp: number, isShort?: boolean): string {
  const [timeAgo, setTimeAgo] = useState<string>('')

  useEffect(() => {
    const updateTimeAgo = () => setTimeAgo(computeTimeAgo(timestamp, isShort))

    updateTimeAgo()
    const intervalId = setInterval(updateTimeAgo, 60000)

    return () => clearInterval(intervalId)
  }, [isShort, timestamp])

  return timeAgo
}
